import React from "react";
import { motion } from "framer-motion";
import Container from "../components/Container";

/* ─── Palette (alineada con HomePage) ─── */
const C = {
  navy: "#0B1F3A",
  navyLight: "#112B4D",
  navyMid: "#0E2545",
  amber: "#FFC857",
  white: "#F5F7FA",
  grayLight: "#9CA3AF",
};

const SERIF = "'Fraunces', 'Playfair Display', Georgia, serif";
const SANS = "'Inter', 'DM Sans', system-ui, sans-serif";
const MONO = "'JetBrains Mono', 'IBM Plex Mono', ui-monospace, monospace";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, delay, ease: "easeOut" },
});

/* The four rules a client asks about. Everything below them is the detail. */
const RULES = [
  {
    es: {
      title: "Tus datos residen exclusivamente en Microsoft Azure",
      body: "La base de datos, sus copias de seguridad y los registros de acceso se alojan en Microsoft Azure, en la región de Brasil. No se almacenan fuera de esa infraestructura.",
    },
    en: {
      title: "Your data resides exclusively in Microsoft Azure",
      body: "The database, its backups and access logs are hosted in Microsoft Azure, in the Brazil region. They are not stored outside that infrastructure.",
    },
  },
  {
    es: {
      title: "El acceso está restringido a la plataforma y a personal autorizado",
      body: "La base de datos no está expuesta a internet. Solo pueden conectarse los servicios de FARO desplegados en Azure y los integrantes autorizados del equipo técnico.",
    },
    en: {
      title: "Access is restricted to the platform and authorized staff",
      body: "The database is not exposed to the internet. Only FARO services deployed in Azure and authorized members of the technical team can connect.",
    },
  },
  {
    es: {
      title: "Tus datos no salen de Azure",
      body: "Todo el procesamiento, incluido el entrenamiento de los modelos de predicción, se ejecuta dentro de Azure. FARO no descarga ni replica tus datos fuera de esa infraestructura; la exportación solo está disponible para tu organización, desde la plataforma.",
    },
    en: {
      title: "Your data never leaves Azure",
      body: "All processing, including training the forecasting models, runs inside Azure. FARO does not download or replicate your data outside that infrastructure; export is available only to your organization, from the platform.",
    },
  },
  {
    es: {
      title: "Cada acceso queda registrado de forma inalterable",
      body: "Todo acceso del equipo de FARO a la base de datos se registra en un almacenamiento inmutable, que nadie, incluido FARO, puede modificar ni eliminar. El registro está disponible para tu organización cuando lo solicite.",
    },
    en: {
      title: "Every access is recorded and tamper-proof",
      body: "Every access by the FARO team to the database is recorded in immutable storage that no one, FARO included, can change or delete. The record is available to your organization on request.",
    },
  },
];

function Rule({ index, title, body }) {
  return (
    <motion.div {...fadeUp(index * 0.06)} className="rounded-2xl p-6 md:p-7 flex gap-5"
      style={{ background: C.white, boxShadow: "0 8px 24px rgba(11,31,58,0.25)" }}>
      <div className="flex-none text-[34px] md:text-[40px] font-bold leading-none"
        style={{ color: C.amber, fontFamily: SERIF, fontStyle: "italic" }}>
        {index + 1}
      </div>
      <div>
        <h2 className="text-[17px] md:text-[19px] font-bold leading-snug" style={{ color: C.navy, fontFamily: SANS }}>
          {title}
        </h2>
        <p className="mt-2 text-[13.5px] md:text-[14px] leading-[1.65]" style={{ color: "#4B5563", fontFamily: SANS }}>
          {body}
        </p>
      </div>
    </motion.div>
  );
}

/* From the outermost layer (the cloud) to the innermost (a person's account). */
const LAYERS = [
  {
    es: {
      name: "La nube",
      title: "Dónde vive la plataforma",
      items: [
        "Toda la plataforma funciona en Microsoft Azure, en Brasil: la aplicación, la base de datos, las copias de seguridad y los registros.",
        "No existen copias de tus datos fuera de esa infraestructura.",
        "Todo se almacena cifrado, y la base de datos se respalda automáticamente todos los días.",
      ],
    },
    en: {
      name: "The cloud",
      title: "Where the platform lives",
      items: [
        "The whole platform runs on Microsoft Azure, in Brazil: the application, the database, backups and logs.",
        "No copies of your data exist outside that infrastructure.",
        "Everything is stored encrypted, and the database is backed up automatically every day.",
      ],
    },
  },
  {
    es: {
      name: "Las conexiones",
      title: "Cómo viajan tus datos",
      items: [
        "Toda comunicación con FARO viaja cifrada (HTTPS, TLS 1.2 o superior).",
        "La base de datos no es accesible desde internet: solo aceptan conexiones la propia plataforma y redes autorizadas.",
        "Los sistemas que envían datos automáticamente usan una clave propia de tu organización, que podés renovar cuando quieras.",
      ],
    },
    en: {
      name: "The connections",
      title: "How your data travels",
      items: [
        "All communication with FARO is encrypted (HTTPS, TLS 1.2 or higher).",
        "The database is not reachable from the internet: only the platform itself and authorized networks can connect.",
        "Systems that send data automatically use a key of your own organization, which you can renew at any time.",
      ],
    },
  },
  {
    es: {
      name: "La base de datos",
      title: "Quién puede ver qué",
      items: [
        "Cada dato está asociado a tu organización, y la plataforma aplica ese filtro en cada consulta: ningún otro cliente puede verlo.",
        "En FARO, solo el equipo técnico accede a los datos, y únicamente para operar la plataforma y darte soporte.",
        "Cada acceso del equipo queda registrado durante un año en un almacenamiento inalterable: nadie, ni siquiera nosotros, puede borrarlo. Si lo pedís, te informamos quién accedió.",
      ],
    },
    en: {
      name: "The database",
      title: "Who can see what",
      items: [
        "Every record is tied to your organization, and the platform applies that filter to every query: no other client can see it.",
        "At FARO, only the technical team accesses the data, and only to run the platform and support you.",
        "Every access by the team is recorded for one year in tamper-proof storage: no one, not even us, can delete it. On request, we tell you who accessed your data.",
      ],
    },
  },
  {
    es: {
      name: "Tu organización",
      title: "Tus datos",
      items: [
        "Procesamos los datos operativos de tu empresa (catálogo, ventas, stock y pedidos) y el nombre y correo de tus usuarios. Nada de tus clientes finales ni de medios de pago.",
        "Los usamos solo para darte el servicio. Tus modelos de predicción se entrenan únicamente con tus datos.",
        "Si dejás FARO, te entregamos tus datos y los eliminamos a tu pedido.",
      ],
    },
    en: {
      name: "Your organization",
      title: "Your data",
      items: [
        "We process your company's operational data (catalog, sales, stock and orders) and your users' names and emails. Nothing about your end customers or payment methods.",
        "We only use it to provide the service. Your forecasting models are trained on your data alone.",
        "If you leave FARO, we hand your data back and delete it on request.",
      ],
    },
  },
  {
    es: {
      name: "Tu cuenta",
      title: "Cada persona de tu empresa",
      items: [
        "Cada persona entra con su propia cuenta, como administrador o con acceso de solo lectura.",
        "La sesión se cierra sola tras una hora de inactividad.",
        "Las contraseñas se almacenan mediante un hash criptográfico irreversible: ni siquiera FARO puede conocerlas.",
      ],
    },
    en: {
      name: "Your account",
      title: "Each person at your company",
      items: [
        "Each person signs in with their own account, as an administrator or with read-only access.",
        "Sessions close automatically after one hour of inactivity.",
        "Passwords are stored using an irreversible cryptographic hash: not even FARO can know them.",
      ],
    },
  },
];

/* Each layer sits inside the one before it: the page reads from the outside in. */
function Layer({ index, name, title, items, children }) {
  return (
    <motion.div {...fadeUp(0.05)} className="rounded-2xl p-3 sm:p-6 md:p-7"
      style={{
        background: index % 2 ? C.navy : C.navyLight,
        border: `1px solid rgba(255,200,87,${0.14 + index * 0.08})`,
      }}>
      <span className="text-[10.5px] font-bold uppercase tracking-[0.22em]" style={{ color: C.amber, fontFamily: MONO }}>
        {String(index + 1).padStart(2, "0")} · {name}
      </span>
      <h2 className="mt-2 text-[18px] md:text-[20px] font-bold leading-tight" style={{ color: C.white, fontFamily: SANS }}>
        {title}
      </h2>
      <ul className="mt-3 space-y-2">
        {items.map((item) => (
          <li key={item} className="flex gap-3 text-[13.5px] md:text-[14px] leading-[1.65]" style={{ color: C.grayLight, fontFamily: SANS }}>
            <span className="mt-[9px] h-1 w-1 flex-none rounded-full" style={{ background: C.amber }} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
      {children && <div className="mt-5">{children}</div>}
    </motion.div>
  );
}

export default function SecurityPage({ language }) {
  const en = language === "en";
  const layers = LAYERS.map((l) => (en ? l.en : l.es));

  // Innermost first, so each layer wraps everything below it.
  const nested = layers.reduceRight(
    (inner, layer, i) => <Layer index={i} {...layer}>{inner}</Layer>,
    null
  );

  return (
    <section className="relative pt-12 md:pt-16 pb-16 md:pb-20 overflow-hidden"
      style={{ background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 100%)` }}>
      <div className="absolute top-0 left-0 w-full h-1" style={{ background: C.amber }} />
      <Container className="relative z-10">
        <motion.div {...fadeUp(0)} className="max-w-3xl mx-auto text-center mb-8 md:mb-10">
          <h1 className="text-[26px] md:text-[34px] lg:text-[40px] font-bold leading-[1.15]"
            style={{ color: C.white, fontFamily: SANS, letterSpacing: "-0.015em" }}>
            {en ? "How we take care of your " : "Cómo cuidamos tus "}
            <span style={{ color: C.amber, fontFamily: SERIF, fontStyle: "italic", fontWeight: 500 }}>
              {en ? "data" : "datos"}
            </span>
          </h1>
          <div className="h-0.5 rounded-full mt-5 mx-auto" style={{ width: "72px", background: C.amber }} />
          <p className="mt-6 text-[14px] md:text-[15px] leading-[1.7] max-w-2xl mx-auto" style={{ color: C.grayLight, fontFamily: SANS }}>
            {en
              ? "Principles FARO applies to the processing of your organization's data."
              : "Principios que FARO aplica al tratamiento de los datos de tu organización."}
          </p>
        </motion.div>

        <div className="grid gap-5 md:grid-cols-2 max-w-5xl mx-auto">
          {RULES.map((r, i) => (
            <Rule key={r.es.title} index={i} {...(en ? r.en : r.es)} />
          ))}
        </div>

        <motion.div {...fadeUp(0)} className="max-w-3xl mx-auto text-center mt-16 md:mt-20 mb-8">
          <span className="text-[10.5px] font-bold uppercase tracking-[0.22em]" style={{ color: C.amber, fontFamily: MONO }}>
            {en ? "In detail" : "En detalle"}
          </span>
          <h2 className="mt-3 text-[22px] md:text-[28px] font-bold leading-tight" style={{ color: C.white, fontFamily: SANS }}>
            {en ? "Protection in layers" : "Protección en capas"}
          </h2>
          <p className="mt-4 text-[14px] md:text-[15px] leading-[1.7]" style={{ color: C.grayLight, fontFamily: SANS }}>
            {en
              ? "Controls behind the four rules, from the infrastructure to user accounts."
              : "Controles que sostienen las cuatro reglas, desde la infraestructura hasta las cuentas de usuario."}
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">{nested}</div>

        <motion.div {...fadeUp(0.1)} className="mt-10 flex flex-col items-center gap-4 text-center">
          <p className="max-w-2xl text-[14px] leading-[1.7]" style={{ color: C.grayLight, fontFamily: SANS }}>
            {en
              ? "If an incident affects your data, we notify you and tell you what happened and what we did about it."
              : "Si ocurre un incidente que afecte tus datos, te avisamos y te contamos qué pasó y qué hicimos al respecto."}
          </p>
          <a href="/seguridad-faro.pdf" target="_blank" rel="noopener noreferrer"
            className="rounded-xl text-sm md:text-base px-7 py-3 font-bold transition-all hover:-translate-y-0.5"
            style={{ background: C.amber, color: C.navy, fontFamily: SANS, boxShadow: "0 4px 24px rgba(255,200,87,0.35)" }}>
            {en ? "Full document (PDF, in Spanish) ↓" : "Documento completo (PDF) ↓"}
          </a>
          <p className="text-[14px]" style={{ color: C.grayLight, fontFamily: SANS }}>
            {en ? "Security questions? Write to us at " : "¿Preguntas sobre seguridad? Escribinos a "}
            <a href="mailto:contacto@farolabs.dev" className="underline" style={{ color: C.amber }}>contacto@farolabs.dev</a>
          </p>
        </motion.div>
      </Container>
    </section>
  );
}
